import React, { useState, useMemo, useRef } from 'react';
import {
  Sparkles, Loader2, Download, Plus, Trash2, CheckCircle2,
  XCircle, AlertTriangle, ListChecks, FlaskConical, Upload,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SCHEMA_OPTIONS = [
  { value: 'labor', label: 'MVZ-Labore', icon: FlaskConical, metricLabel: 'MA' },
  { value: 'krankenhaus', label: 'Kliniken', icon: FlaskConical, metricLabel: 'Betten' },
];

const STATE_OPTIONS = [
  { value: 'BW', label: 'Baden-Württemberg' },
  { value: 'BY', label: 'Bayern' },
  { value: 'BE', label: 'Berlin' },
  { value: 'BB', label: 'Brandenburg' },
  { value: 'HB', label: 'Bremen' },
  { value: 'HH', label: 'Hamburg' },
  { value: 'HE', label: 'Hessen' },
  { value: 'MV', label: 'Mecklenburg-Vorpommern' },
  { value: 'NI', label: 'Niedersachsen' },
  { value: 'NW', label: 'Nordrhein-Westfalen' },
  { value: 'RP', label: 'Rheinland-Pfalz' },
  { value: 'SL', label: 'Saarland' },
  { value: 'SN', label: 'Sachsen' },
  { value: 'ST', label: 'Sachsen-Anhalt' },
  { value: 'SH', label: 'Schleswig-Holstein' },
  { value: 'TH', label: 'Thüringen' },
];

const SCHEMA_TO_TYPE = {
  labor: 'labor',
  krankenhaus: 'krankenhaus',
};

const resultBadge = (r) => {
  const v = (r || '').toUpperCase();
  if (v === 'TYP A') return { cls: 'bg-emerald-100 text-emerald-800 border-emerald-300', label: 'TYP A' };
  if (v === 'TYP A*') return { cls: 'bg-lime-100 text-lime-800 border-lime-300', label: 'TYP A*' };
  if (v === 'TYP B') return { cls: 'bg-amber-100 text-amber-800 border-amber-300', label: 'TYP B' };
  return { cls: 'bg-rose-100 text-rose-800 border-rose-300', label: 'RAUS' };
};

const PreQualTab = ({ password }) => {
  const [mode, setMode] = useState('batch'); // 'single' | 'batch' | 'csv'
  const [schema, setSchema] = useState('labor');
  const [state, setState] = useState('BW');

  // Single input
  const [singleName, setSingleName] = useState('');
  const [singleCity, setSingleCity] = useState('');

  // Batch input (one per line: "Name; Stadt" or just "Name")
  const [batchText, setBatchText] = useState('');

  // CSV input — Outreach-Schema (name;size_metric;street;zip;city;…)
  const csvFileInputRef = useRef(null);
  const [csvFileName, setCsvFileName] = useState('');
  const [csvRows, setCsvRows] = useState([]); // [{name, city, ...}]
  const [csvError, setCsvError] = useState('');

  const [running, setRunning] = useState(false);
  const [runProgress, setRunProgress] = useState({ done: 0, total: 0 });
  const [error, setError] = useState('');
  const [results, setResults] = useState([]); // list of result items
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState('');

  const headers = {
    'Content-Type': 'application/json',
    'x-admin-password': password,
  };

  const metricLabel = useMemo(
    () => SCHEMA_OPTIONS.find((o) => o.value === schema)?.metricLabel || 'MA',
    [schema]
  );

  const candidatesFromInput = useMemo(() => {
    if (mode === 'single') {
      if (!singleName.trim()) return [];
      return [{ name: singleName.trim(), city: singleCity.trim() }];
    }
    if (mode === 'csv') {
      return csvRows;
    }
    return batchText
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const parts = line.split(/[;,]/).map((p) => p.trim());
        return { name: parts[0] || '', city: parts[1] || '' };
      })
      .filter((c) => c.name.length >= 2);
  }, [mode, singleName, singleCity, batchText, csvRows]);

  const aCount = useMemo(
    () => results.filter((r) => ['TYP A', 'TYP A*'].includes((r.result || '').toUpperCase())).length,
    [results]
  );

  // ------------------------------------------------------------------
  // CSV-Parsing (Outreach-Schema: name;size_metric;street;zip;city;…)
  // ------------------------------------------------------------------
  const parseCsvFile = async (file) => {
    setCsvError('');
    setCsvRows([]);
    setCsvFileName(file.name);
    try {
      const text = await file.text();
      // BOM entfernen
      const clean = text.replace(/^\uFEFF/, '');
      const lines = clean.split(/\r?\n/).filter((l) => l.trim().length > 0);
      if (lines.length < 2) {
        throw new Error('CSV enthält keine Datenzeilen.');
      }
      // Trennzeichen-Erkennung: Semikolon bevorzugt (Outreach-Standard), Fallback Komma
      const headerLine = lines[0];
      const delim = headerLine.includes(';') ? ';' : ',';
      const headers = headerLine.split(delim).map((h) => h.trim().toLowerCase().replace(/^"|"$/g, ''));
      const nameIdx = headers.indexOf('name');
      if (nameIdx === -1) {
        throw new Error('Spalte "name" fehlt — CSV muss dem Outreach-Schema entsprechen.');
      }
      const cityIdx = headers.indexOf('city');
      // Alle weiteren MVZ-Felder, die passthrough sollen:
      const idx = (name, ...aliases) => {
        for (const k of [name, ...aliases]) {
          const i = headers.indexOf(k);
          if (i !== -1) return i;
        }
        return -1;
      };
      const phoneIdx = idx('phone', 'telefon', 'phone_office');
      const stateIdx = idx('state', 'bundesland');
      const regionIdx = idx('region', 'vertriebsregion');
      const levelIdx = idx('level', 'ebene', 'größe', 'groesse');
      const streetIdx = idx('street', 'strasse', 'straße');
      const zipIdx = idx('zip', 'plz');
      const sizeIdx = idx('size_metric', 'mitarbeiter', 'betten');
      const contactIdx = idx('contact_person', 'ansprechpartner');
      const contactRoleIdx = idx('contact_role', 'rolle', 'funktion');
      const salutationIdx = idx('salutation', 'anrede');
      const websiteIdx = idx('website', 'homepage');
      const emailIdx = idx('email', 'email1', 'mail');

      const rows = [];
      for (let i = 1; i < lines.length; i++) {
        const cells = lines[i].split(delim).map((c) => c.trim().replace(/^"|"$/g, ''));
        const name = cells[nameIdx] || '';
        if (name.length < 2) continue;
        const row = {
          name,
          city: cityIdx >= 0 ? (cells[cityIdx] || '') : '',
          // pass-through Felder (werden an /csv weitergereicht, NICHT an /run)
          phone_input: phoneIdx >= 0 ? (cells[phoneIdx] || '') : '',
          state_input: stateIdx >= 0 ? (cells[stateIdx] || '').toUpperCase() : '',
          region_input: regionIdx >= 0 ? (cells[regionIdx] || '') : '',
          level_input: levelIdx >= 0 ? (cells[levelIdx] || '') : '',
          street_input: streetIdx >= 0 ? (cells[streetIdx] || '') : '',
          zip_input: zipIdx >= 0 ? (cells[zipIdx] || '') : '',
          size_input: sizeIdx >= 0 ? (cells[sizeIdx] || '') : '',
          contact_input: contactIdx >= 0 ? (cells[contactIdx] || '') : '',
          contact_role_input: contactRoleIdx >= 0 ? (cells[contactRoleIdx] || '') : '',
          salutation_input: salutationIdx >= 0 ? (cells[salutationIdx] || '') : '',
          website_input: websiteIdx >= 0 ? (cells[websiteIdx] || '') : '',
          email_input: emailIdx >= 0 ? (cells[emailIdx] || '') : '',
        };
        rows.push(row);
      }
      if (rows.length === 0) {
        throw new Error('Keine gültigen Datenzeilen gefunden (jede Zeile braucht mind. einen Namen).');
      }
      setCsvRows(rows);
    } catch (e) {
      setCsvError(e.message || 'CSV konnte nicht gelesen werden.');
    }
  };

  const onCsvPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    parseCsvFile(file);
    e.target.value = ''; // erlaubt erneutes Hochladen derselben Datei
  };

  const clearCsv = () => {
    setCsvRows([]);
    setCsvFileName('');
    setCsvError('');
  };

  // ------------------------------------------------------------------
  // Pre-qualification run (chunked to respect backend 20-per-call limit)
  // ------------------------------------------------------------------
  const runPrequal = async () => {
    setError('');
    setImportMsg('');
    if (candidatesFromInput.length === 0) {
      setError('Mindestens einen Namen eingeben.');
      return;
    }
    setRunning(true);
    setResults([]);
    const CHUNK = 20;
    const total = candidatesFromInput.length;
    setRunProgress({ done: 0, total });
    const allResults = [];
    const allErrors = [];
    // Map name → passthrough-Felder, damit wir sie nach dem LLM-Lauf wieder dranhängen können
    const passthroughByName = new Map();
    candidatesFromInput.forEach((c) => {
      const pt = Object.fromEntries(
        Object.entries(c).filter(([k]) => k.endsWith('_input'))
      );
      if (Object.keys(pt).length > 0) passthroughByName.set(c.name, pt);
    });
    try {
      for (let i = 0; i < total; i += CHUNK) {
        const slice = candidatesFromInput
          .slice(i, i + CHUNK)
          .map((c) => ({ name: c.name, city: c.city })); // Backend bekommt nur Name+City
        const res = await fetch(`${API}/admin/prequalification/run`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ schema, candidates: slice }),
        });
        if (!res.ok) {
          const txt = await res.text();
          throw new Error(`HTTP ${res.status}: ${txt}`);
        }
        const data = await res.json();
        // Passthrough-Felder zurück an Ergebnisse anhängen
        for (const r of (data.results || [])) {
          const pt = passthroughByName.get(r.name);
          if (pt) Object.assign(r, pt);
        }
        allResults.push(...(data.results || []));
        if (data.errors && data.errors.length > 0) allErrors.push(...data.errors);
        setResults([...allResults]);
        setRunProgress({ done: Math.min(i + CHUNK, total), total });
      }
      if (allErrors.length > 0) {
        setError(`Fehler bei ${allErrors.length} Einträgen: ${allErrors.map((e) => e.name).join(', ')}`);
      }
    } catch (e) {
      setError(e.message || 'Fehler bei der Vorqualifikation.');
    } finally {
      setRunning(false);
    }
  };

  const downloadCsv = async (onlyTypA = true) => {
    if (results.length === 0) return;
    setError('');
    try {
      const res = await fetch(`${API}/admin/prequalification/csv`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ schema, results, only_typ_a: onlyTypA }),
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`HTTP ${res.status}: ${txt}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vorqualifikation-${schema}-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message || 'CSV-Download fehlgeschlagen.');
    }
  };

  const importDirectly = async () => {
    if (aCount === 0) return;
    setImporting(true);
    setImportMsg('');
    setError('');
    try {
      // 1. Build CSV via backend
      const csvRes = await fetch(`${API}/admin/prequalification/csv`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ schema, results, only_typ_a: true }),
      });
      if (!csvRes.ok) throw new Error('CSV-Erzeugung fehlgeschlagen.');
      const blob = await csvRes.blob();

      // 2. POST to existing outreach import endpoint
      const form = new FormData();
      form.append('file', blob, `prequal-${schema}.csv`);
      const importType = SCHEMA_TO_TYPE[schema] || 'sonstige';
      const importUrl = `${API}/admin/outreach/targets/import-csv?type=${importType}&state=${state}`;
      const importRes = await fetch(importUrl, {
        method: 'POST',
        headers: { 'x-admin-password': password },
        body: form,
      });
      // Body genau einmal lesen, dann JSON-parsen — robust gegen "body stream already read"
      const rawImport = await importRes.text();
      let importData;
      try {
        importData = JSON.parse(rawImport);
      } catch {
        throw new Error(`Server antwortete nicht im JSON-Format (HTTP ${importRes.status}): ${rawImport.slice(0, 200)}`);
      }
      if (!importRes.ok) {
        throw new Error(importData.detail || `Import-Fehler (HTTP ${importRes.status})`);
      }
      setImportMsg(`✓ ${importData.inserted} Leads importiert (Status: new). Übersprungen: ${importData.skipped?.length || 0}.`);
    } catch (e) {
      setError(e.message || 'Direktimport fehlgeschlagen.');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="prequal-tab">
      {/* Header */}
      <div className="bg-gradient-to-br from-violet-50 to-fuchsia-50 border border-violet-200 rounded-lg p-5">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-lg bg-violet-600 text-white flex items-center justify-center">
            <Sparkles size={20} />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-slate-900">Vorqualifikations-Agent</h2>
            <p className="text-sm text-slate-600">
              Web-Recherche + KI-Bewertung nach festem Branchen-Schema → CSV kompatibel zum Outreach-Import.
            </p>
          </div>
        </div>
      </div>

      {/* Config row */}
      <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Schema</label>
            <select
              value={schema}
              onChange={(e) => setSchema(e.target.value)}
              className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
              data-testid="prequal-schema-select"
            >
              {SCHEMA_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-700 uppercase tracking-wide">
              Bundesland (für späteren Import)
            </label>
            <select
              value={state}
              onChange={(e) => setState(e.target.value)}
              className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
              data-testid="prequal-state-select"
            >
              {STATE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Modus</label>
            <div className="mt-1 flex rounded-md border border-slate-300 overflow-hidden">
              <button
                type="button"
                onClick={() => setMode('single')}
                className={`flex-1 px-3 py-2 text-sm font-medium ${
                  mode === 'single' ? 'bg-slate-800 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'
                }`}
                data-testid="prequal-mode-single"
              >
                Einzeln
              </button>
              <button
                type="button"
                onClick={() => setMode('batch')}
                className={`flex-1 px-3 py-2 text-sm font-medium border-l border-slate-300 ${
                  mode === 'batch' ? 'bg-slate-800 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'
                }`}
                data-testid="prequal-mode-batch"
              >
                Batch
              </button>
              <button
                type="button"
                onClick={() => setMode('csv')}
                className={`flex-1 px-3 py-2 text-sm font-medium border-l border-slate-300 ${
                  mode === 'csv' ? 'bg-slate-800 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'
                }`}
                data-testid="prequal-mode-csv"
              >
                CSV
              </button>
            </div>
          </div>
        </div>

        {/* Input */}
        {mode === 'single' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input
              placeholder="Name der Einrichtung (z. B. MVZ Labor Prof. Blessing Singen GmbH)"
              value={singleName}
              onChange={(e) => setSingleName(e.target.value)}
              data-testid="prequal-single-name"
            />
            <Input
              placeholder="Stadt (optional, verbessert die Recherche)"
              value={singleCity}
              onChange={(e) => setSingleCity(e.target.value)}
              data-testid="prequal-single-city"
            />
          </div>
        ) : mode === 'csv' ? (
          <div>
            <div className="flex flex-wrap items-center gap-3 mb-2">
              <input
                ref={csvFileInputRef}
                type="file"
                accept=".csv,text/csv"
                onChange={onCsvPick}
                className="hidden"
                data-testid="prequal-csv-file-input"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => csvFileInputRef.current?.click()}
                data-testid="prequal-csv-pick-btn"
              >
                <Upload size={14} className="mr-2" />
                CSV-Datei wählen
              </Button>
              {csvFileName && (
                <span className="text-sm text-slate-600 flex items-center gap-2">
                  <span className="font-medium text-slate-800">{csvFileName}</span>
                  <button
                    type="button"
                    onClick={clearCsv}
                    className="text-slate-400 hover:text-rose-600"
                    title="CSV entfernen"
                    data-testid="prequal-csv-clear-btn"
                  >
                    <XCircle size={14} />
                  </button>
                </span>
              )}
            </div>
            <p className="text-xs text-slate-500">
              Erwartet das <strong>Outreach-CSV-Schema</strong> (Semikolon-getrennt) mit Spalte <code className="px-1 bg-slate-100 rounded">name</code> als Pflicht, <code className="px-1 bg-slate-100 rounded">city</code> als Empfehlung. Weitere Spalten werden ignoriert.
            </p>
            {csvRows.length > 0 && (
              <div className="mt-2 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded p-2">
                <strong>{csvRows.length}</strong> Zeile{csvRows.length === 1 ? '' : 'n'} erkannt
                {csvRows.length > 0 && (
                  <span className="text-slate-500">
                    {' '}· Vorschau:{' '}
                    {csvRows.slice(0, 3).map((r) => r.name).join(' · ')}
                    {csvRows.length > 3 ? ` … (+${csvRows.length - 3})` : ''}
                  </span>
                )}
              </div>
            )}
            {csvError && (
              <div className="mt-2 flex items-start gap-2 bg-rose-50 border border-rose-200 text-rose-800 rounded-md px-3 py-2 text-xs">
                <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                <span>{csvError}</span>
              </div>
            )}
          </div>
        ) : (
          <div>
            <Textarea
              placeholder={schema === 'krankenhaus'
                ? 'Eine Klinik pro Zeile, optional mit Stadt nach Semikolon:\nVidia Kliniken; Karlsruhe\nSiloah St. Trudpert Klinikum; Pforzheim\nHelios Klinikum Berlin-Buch; Berlin'
                : 'Ein Labor pro Zeile, optional mit Stadt nach Semikolon:\nMVZ Labor Prof. Blessing Singen GmbH; Singen\nMVZ Labor Ludwigsburg SE & Co. eGbR; Ludwigsburg\nBioscientia MVZ Labor Saar GmbH; St. Ingbert'}
              value={batchText}
              onChange={(e) => setBatchText(e.target.value)}
              className="min-h-[140px] font-mono text-sm"
              data-testid="prequal-batch-input"
            />
            <p className="text-xs text-slate-500 mt-1">
              {candidatesFromInput.length} Einträge erkannt
            </p>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <Button
            onClick={runPrequal}
            disabled={running || candidatesFromInput.length === 0}
            className="bg-violet-600 hover:bg-violet-700 text-white"
            data-testid="prequal-run-btn"
          >
            {running ? (
              <><Loader2 size={16} className="animate-spin mr-2" /> Recherche läuft… {runProgress.total > 0 ? `(${runProgress.done}/${runProgress.total})` : ''}</>
            ) : (
              <><Sparkles size={16} className="mr-2" /> Vorqualifikation starten{candidatesFromInput.length > 0 ? ` (${candidatesFromInput.length})` : ''}</>
            )}
          </Button>
          {results.length > 0 && (
            <span className="text-sm text-slate-600">
              <ListChecks size={14} className="inline mr-1" />
              {results.length} bewertet · <strong className="text-emerald-700">{aCount}</strong> Treffer (TYP A/A*)
            </span>
          )}
        </div>

        {error && (
          <div className="flex items-start gap-2 bg-rose-50 border border-rose-200 text-rose-800 rounded-md px-3 py-2 text-sm">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}
        {importMsg && (
          <div className="flex items-start gap-2 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-md px-3 py-2 text-sm">
            <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
            <span>{importMsg}</span>
          </div>
        )}
      </div>

      {/* Results */}
      {results.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-slate-200">
            <h3 className="font-semibold text-slate-900">Ergebnisse</h3>
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={() => downloadCsv(true)}
                variant="outline"
                size="sm"
                data-testid="prequal-csv-typa-btn"
              >
                <Download size={14} className="mr-2" /> CSV (nur TYP A/A*)
              </Button>
              <Button
                onClick={() => downloadCsv(false)}
                variant="outline"
                size="sm"
                data-testid="prequal-csv-all-btn"
              >
                <Download size={14} className="mr-2" /> CSV (alle)
              </Button>
              <Button
                onClick={importDirectly}
                disabled={importing || aCount === 0}
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="prequal-import-btn"
              >
                {importing ? (
                  <><Loader2 size={14} className="animate-spin mr-2" /> Importiere…</>
                ) : (
                  <><Plus size={14} className="mr-2" /> {aCount} direkt in Outreach</>
                )}
              </Button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 uppercase text-xs tracking-wide">
                <tr>
                  <th className="text-left px-4 py-3">Einrichtung</th>
                  <th className="text-left px-4 py-3">Ergebnis</th>
                  <th className="text-left px-4 py-3">{metricLabel}</th>
                  <th className="text-left px-4 py-3">Verbund / Träger</th>
                  <th className="text-left px-4 py-3">Entscheider</th>
                  <th className="text-left px-4 py-3">Volumen/Jahr</th>
                  <th className="text-left px-4 py-3">Nächster Schritt</th>
                </tr>
              </thead>
              <tbody>
                {results.map((r, i) => {
                  const badge = resultBadge(r.result);
                  const isRaus = (r.result || '').toUpperCase() === 'RAUS';
                  return (
                    <tr key={`${r.name}-${i}`} className="border-t border-slate-100 align-top">
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">{r.name}</div>
                        {r.city && (
                          <div className="text-xs text-slate-500">{r.city}</div>
                        )}
                        {r.reasoning && !isRaus && (
                          <div className="text-xs text-slate-600 mt-1 max-w-md">{r.reasoning}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2 py-0.5 text-xs font-semibold border rounded ${badge.cls}`}>
                          {badge.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {r.employees ? (
                          <>
                            <div>{r.employees}</div>
                            {r.employees_source && (
                              <div className="text-xs text-slate-500">{r.employees_source}</div>
                            )}
                          </>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">{r.group || '—'}</td>
                      <td className="px-4 py-3">
                        {r.contact_person ? (
                          <>
                            <div className="font-medium">{r.contact_person}</div>
                            {r.contact_role && (
                              <div className="text-xs text-slate-500">{r.contact_role}</div>
                            )}
                          </>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {r.volume_estimate ? `${r.volume_estimate.toLocaleString('de-DE')}` : '—'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {isRaus ? (
                          <span className="inline-flex items-center gap-1 text-rose-700">
                            <XCircle size={14} /> Nicht kontaktieren
                          </span>
                        ) : (
                          <span className="text-slate-700">{r.next_step || '—'}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-5 py-3 border-t border-slate-100 bg-slate-50 text-xs text-slate-600 flex items-center gap-2">
            <Trash2
              size={12}
              className="cursor-pointer hover:text-rose-600"
              onClick={() => { setResults([]); setImportMsg(''); }}
              data-testid="prequal-clear-results"
            />
            <span>
              Ergebnisse leeren · Direktimport setzt Status <code className="px-1 bg-white border border-slate-200 rounded">new</code> und Wiedervorlage automatisch (TYP A: +14 Tage, TYP A*: +30 Tage).
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default PreQualTab;
